import { z } from 'zod'

/**
 * Schemas de validation des mutations « e-mails de programme » (plan §9.2).
 *
 * Ce fichier est le SEUL endroit ou la forme des corps envoyes a l'API est decrite : les
 * ecrans construisent leur etat librement, mais rien ne part sans passer par un `parse`.
 *
 * Les valeurs acceptees suivent le contrat SERVEUR (`ProgramEmailAudienceRules`,
 * `ProgramEmailDraftInput`), pas la maquette : `completion` accepte les trois options du
 * DTO, et le corps de `draft` porte ses regles de ciblage dans un objet `rules` imbrique,
 * exactement comme `ProgramEmailDraftInput::fromArray` les lit.
 */

/** Meme forme canonique que `CipNormalizer::PATTERN` cote API. */
const cip = z.string().regex(/^(?:\d{7,13}|PH\d{5})$/)

/** Plafond commun aux listes manuelles et aux regles de ciblage (`CipNormalizer::MAX_CIPS`). */
export const PROGRAM_EMAIL_MAX_CIPS = 5000

/**
 * Perimetre d'un envoi : module du programme, campagne partenaire entiere (11/09/2026), ou
 * newsletter d'un spot public (29/10/2026).
 */
export const programEmailModule = z.enum([
  'sound',
  'storebag',
  'insight',
  'partner_campaign',
  'sound_public_spot',
])


export const programEmailOutcome = z.enum([
  'notAccepted',
  'notOrdered',
  'notCompleted',
  'notResponded',
  'notSigned',
  'notCaptured',
])

/**
 * Matrice D-26 : outcomes applicables a chaque module, dans l'ordre du serveur
 * (`ProgramEmailOutcome::modules()`). Proposer un outcome hors matrice fait repondre 422
 * `outcomeNotApplicable` — l'ecran ne doit donc jamais lister les six sans filtrer.
 */
export const PROGRAM_EMAIL_OUTCOMES_BY_MODULE: Record<
  z.infer<typeof programEmailModule>,
  Array<z.infer<typeof programEmailOutcome>>
> = {
  sound: ['notAccepted', 'notCompleted', 'notSigned', 'notCaptured'],
  storebag: ['notAccepted', 'notOrdered', 'notCompleted', 'notSigned', 'notCaptured'],
  insight: ['notCompleted', 'notResponded'],
  // Campagne partenaire : les seules notions portees par la CAMPAGNE (decision, contrat
  // mutualise). Commande, preuve photo et avancement restent des notions de programme.
  partner_campaign: ['notAccepted', 'notSigned'],
  // Newsletter d'un spot public : ni participation, ni commande, ni avancement.
  sound_public_spot: [],
}

export const programEmailDecision = z.enum(['exclude_rejected', 'all', 'pending_only'])

export const programEmailCompletion = z.enum(['all', 'completed', 'not_completed'])

/**
 * Commande automatique PLV (`ProgramEmailAudienceRules::AUTO_ORDERS`). Le serveur refuse le
 * critere hors module `storebag` (422) : les ecrans ne le proposent que pour la PLV, et
 * n'envoient la cle que lorsqu'elle filtre (`only` / `exclude`).
 */
export const programEmailAutoOrder = z.enum(['all', 'only', 'exclude'])

/**
 * Filtres d'ecran WHITELISTES cote serveur : tout autre nom est refuse en 422. La cle
 * `company.cip` porte le point, elle reste donc entre guillemets.
 */
/**
 * Valeurs fermees des filtres de CAMPAGNE PARTENAIRE (`ProgramEmailAudienceRules::*_FILTER_VALUES`).
 * `segmentation` est libre : c'est le libelle du sous-groupe du laboratoire.
 */
export const PROGRAM_EMAIL_CAMPAIGN_FILTER_VALUES = {
  module: ['sound', 'storebag'],
  cadre: ['signed', 'pending', 'not_required'],
  contract: ['signed', 'unsigned', 'not_required', 'forced'],
  proof: ['pending', 'done', 'none'],
} as const

/**
 * Filtres de campagne partenaire : ceux de la page de suivi, appliques par le serveur avec
 * les memes verdicts. Refuses (422) hors du perimetre `partner_campaign`.
 */
export const programEmailCampaignFiltersSchema = z.object({
  module: z.enum(PROGRAM_EMAIL_CAMPAIGN_FILTER_VALUES.module).optional(),
  segmentation: z.string().min(1).optional(),
  cadre: z.enum(PROGRAM_EMAIL_CAMPAIGN_FILTER_VALUES.cadre).optional(),
  contract: z.enum(PROGRAM_EMAIL_CAMPAIGN_FILTER_VALUES.contract).optional(),
  proof: z.enum(PROGRAM_EMAIL_CAMPAIGN_FILTER_VALUES.proof).optional(),
})

export const programEmailFiltersSchema = programEmailCampaignFiltersSchema.extend({
  participationDecision: z.string().optional(),
  'company.cip': z.string().optional(),
  // Filtres de la newsletter d'un spot seulement, refuses ailleurs (422) : groupement (id de
  // la fiche), abonnement Sound, distributeur de la radio.
  groupement: z.string().regex(/^\d+$/).optional(),
  sound: z.enum(['active', 'inactive', 'all']).optional(),
  distributor: z.enum(['saooti', 'radioshop', 'pharmaradio', 'none']).optional(),
})

/**
 * Regles de ciblage gelees dans `program_email_campaign.audience_rules` — l'objet `rules`
 * du corps de `draft`. `minDaysSinceProofRequest` n'a de sens qu'avec l'outcome
 * `notCaptured`.
 *
 * Plus aucune borne d'anti-sur-sollicitation n'est SAISIE : ni la fenetre glissante
 * `skipRecentlyContactedDays`, ni la date fixe `skipContactedSince`. Le serveur les
 * accepte et les applique toujours pour les campagnes et regles deja enregistrees, et
 * continue de SIGNALER les officines recemment contactees sans les ecarter.
 */
export const programEmailAudienceRulesSchema = z.object({
  decision: programEmailDecision.default('exclude_rejected'),
  completion: programEmailCompletion.default('all'),
  outcome: programEmailOutcome.optional(),
  budgetIds: z.array(z.number().int().positive()).max(PROGRAM_EMAIL_MAX_CIPS).optional(),
  filters: programEmailFiltersSchema.optional(),
  includeCips: z.array(cip).max(PROGRAM_EMAIL_MAX_CIPS).default([]),
  excludeCips: z.array(cip).max(PROGRAM_EMAIL_MAX_CIPS).default([]),
  minDaysSinceProofRequest: z.number().int().min(0).max(90).nullable().optional(),
  autoOrder: programEmailAutoOrder.optional(),
  /** Anteriorite chez le laboratoire : all (defaut) | first (nouvelles) | returning (deja clientes). */
  partnerHistory: z.enum(['all', 'first', 'returning']).optional(),
})

/**
 * Identite d'un ciblage : module, programme, contenu et nature choisie par l'auteur.
 *
 * Le CONTENU prend l'une des deux formes admises par l'API : `bodyTemplateId` (modele de corps
 * redige chez nous, injecte dans la coquille Mailjet) ou `templateId` (template Mailjet portant
 * tout le message, forme historique). `templateId` reste donc optionnel des lors qu'un corps
 * est choisi — le refus d'un envoi sans contenu appartient au serveur, qui seul connait l'etat
 * reel des deux catalogues.
 */
export const programEmailAudienceSchema = z.object({
  module: programEmailModule,
  programId: z.number().int().positive(),
  templateId: z.number().int().positive().optional(),
  bodyTemplateId: z.number().int().positive().optional(),
})

/**
 * `POST program_email_campaigns/draft`. `draftId` actualise un brouillon existant (les
 * exclusions manuelles sont conservees cote serveur) ; `templateName` vient du selecteur,
 * l'API ne peut pas le relire depuis Mailjet.
 */
export const programEmailDraftSchema = programEmailAudienceSchema.extend({
  draftId: z.number().int().positive().optional(),
  name: z.string().min(1).max(150).optional(),
  templateName: z.string().max(255).optional(),
  rules: programEmailAudienceRulesSchema,
})

/**
 * `POST` / `PATCH program_email_body_templates`. Les jetons `{{cle}}` du sujet et du corps ne
 * sont PAS valides ici : le catalogue des champs vit cote serveur, qui refuse (422) toute cle
 * inconnue. Dupliquer la liste dans un schema Zod la ferait diverger au premier champ ajoute.
 */
export const programEmailBodyTemplateSchema = z.object({
  module: programEmailModule.optional(),
  purpose: z
    .enum(['information', 'invitation', 'deadline_reminder', 'order_reminder', 'photo_proof'])
    .optional(),
  name: z.string().min(1).max(190),
  subject: z.string().min(1).max(255),
  bodyHtml: z.string().min(1),
  active: z.boolean().optional(),
})

/** `POST program_email_campaigns/resolve_cips` — statut de chaque CIP colle ou importe. */
export const programEmailResolveCipsSchema = z.object({
  module: programEmailModule,
  programId: z.number().int().positive(),
  cips: z.array(z.string()).max(PROGRAM_EMAIL_MAX_CIPS),
})

/**
 * `POST program_email_campaigns/{id}/test`. Plusieurs testeurs depuis le 15/09/2026 : la cle
 * du corps est `emails` (liste) — `ProgramEmailTestRecipients` la lit cote serveur, avec la
 * meme borne de dix adresses.
 */
export const programEmailTestSchema = z.object({
  emails: z.array(z.string().email()).min(1).max(10),
  recipientId: z.number().int().positive().optional(),
})

/** `POST program_email_campaigns/{id}/recipients/{recipientId}/exclusion`. */
export const programEmailRecipientExclusionSchema = z.object({
  excluded: z.boolean(),
})

/** `POST program_email_campaigns/{id}/dispatch` — aucun corps, la campagne suffit. */
export const programEmailDispatchSchema = z.object({})

/** `POST program_email_campaigns/{id}/cancel` et `.../revalidate` — aucun corps. */
export const programEmailEmptyBodySchema = z.object({})

/**
 * `POST program_email_campaigns/messages/:customId/resend` — `email` : une des adresses
 * proposees par l'apercu (`resendTargets`) ; absente, l'adresse retenue par defaut.
 */
export const programEmailResendSchema = z.object({
  email: z.string().email().optional(),
})

/**
 * `POST program_email_campaigns/{id}/recipients/export` — les FILTRES DE L'ECRAN, memes cles
 * que `GET .../recipients`. Le serveur ne lit rien d'autre : un filtre absent vaut « pas de
 * filtre », jamais un defaut de confort.
 *
 * `kept` reste `boolean` (et non un tri-etat textuel) : cote serveur, seul un booleen strict
 * filtre, toute autre valeur est ignoree. L'ecran envoie donc la cle uniquement quand
 * l'utilisateur a choisi « retenus » ou « ecartes ».
 */
export const programEmailRecipientsExportSchema = z.object({
  kept: z.boolean().optional(),
  status: z.string().min(1).optional(),
  mailjetStatus: z.string().min(1).optional(),
  search: z.string().min(1).optional(),
})

/**
 * Regles d'une regle programmee : memes options que le ciblage manuel, MOINS `budgetIds`
 * et les filtres d'ECRAN — une regle re-resout sa population a l'instant T (§18.1), elle
 * ne fige pas une selection d'ecran.
 */
export const programEmailScheduleRulesSchema = programEmailAudienceRulesSchema
  .omit({
    budgetIds: true,
    filters: true,
  })
  // Les filtres de CAMPAGNE, eux, sont des criteres relus a chaque execution : une regle
  // programmee peut les porter (jamais les filtres d'ecran).
  .extend({ filters: programEmailCampaignFiltersSchema.optional() })

/**
 * `POST program_email_schedules`. `runAt` DOIT porter un offset explicite (jamais une
 * chaine locale nue) : c'est `lib/programEmail/programEmailTimezone.ts` qui le construit,
 * en interpretant la saisie comme une heure de PARIS quel que soit le fuseau du poste.
 * `name` omis = le serveur applique son defaut « {modele} — {date d'execution} » (§18.1).
 */
export const programEmailScheduleSchema = programEmailAudienceSchema.extend({
  name: z.string().min(1).max(150).optional(),
  runAt: z.string().datetime({ offset: true }),
  validatorUserIds: z.array(z.number().int().positive()).max(10).default([]),
  rules: programEmailScheduleRulesSchema,
})

/** `PATCH program_email_schedules/{id}` — memes champs, tous optionnels (merge-patch). */
export const programEmailScheduleUpdateSchema = programEmailScheduleSchema.partial()

/**
 * `POST program_email_schedules/estimate` — dry-run, aucune persistance. `runAt` est
 * optionnel : l'estimation porte sur le ciblage, pas sur la date d'execution.
 */
export const programEmailScheduleEstimateSchema = programEmailAudienceSchema.extend({
  rules: programEmailScheduleRulesSchema,
  runAt: z.string().datetime({ offset: true }).optional(),
})

export type ProgramEmailAudienceRulesInput = z.input<typeof programEmailAudienceRulesSchema>
export type ProgramEmailAudienceRulesPayload = z.output<typeof programEmailAudienceRulesSchema>
export type ProgramEmailDraftPayload = z.output<typeof programEmailDraftSchema>
export type ProgramEmailBodyTemplatePayload = z.output<typeof programEmailBodyTemplateSchema>
export type ProgramEmailTestPayload = z.output<typeof programEmailTestSchema>
export type ProgramEmailScheduleRulesPayload = z.output<typeof programEmailScheduleRulesSchema>
export type ProgramEmailSchedulePayload = z.output<typeof programEmailScheduleSchema>

/**
 * Message lisible d'une erreur levée par `fetchRest`.
 *
 * `fetchRest` construit une `Error` dont le `message` porte DÉJÀ le détail rendu par l'API
 * (`detail`, `message`, `hydra:description`), et attache `status` et `data`. Lire
 * `err.response.data` — la forme d'axios — rend toujours `undefined` et fait retomber sur un
 * message générique : le serveur nomme les jetons de fusion fautifs, et cette information était
 * perdue avant d'arriver à l'auteur.
 */
export const restErrorMessage = (error: unknown, fallback: string): string => {
  const message = (error as { message?: string } | null)?.message

  return typeof message === 'string' && message.trim() !== '' ? message : fallback
}
